/**
 * What a season that has not started yet can honestly show.
 *
 * Before the first ball there is nothing to rank, and a leaderboard of noughts
 * ranks nobody — so the section says what is coming instead. Saying it is worth
 * something on its own: a player who does not know a rating will move has no
 * reason to look again on Monday.
 *
 * But the strongest thing on the page is not a description. It is the season
 * BEFORE, which is full of the same people's names and already has every one of
 * these boards populated. "Here is what this looks like" beats "here is what
 * this will do", and it costs nothing because the data is already there.
 */

/** The barest shape of a season this needs, so a test does not build a League. */
export interface PreviewableSeason {
  id: number;
  name: string;
  archived: boolean;
  stats?: { matchesPlayed: number } | undefined;
}

/**
 * A finished season worth pointing at, or null if there is none.
 *
 * Archived AND actually played. An archived season with no results is a season
 * that was set up and abandoned, and sending someone to look at it would be the
 * same empty page they are already on — worse, because they went there expecting
 * something.
 *
 * The most-played one when there are several, because that is the fullest
 * example, not the newest: a season that ended after two rounds shows less than
 * one that ran a year earlier and finished.
 */
export function playedSeason<T extends PreviewableSeason>(
  seasons: readonly T[] | undefined,
  exclude?: number,
): T | null {
  const played = (seasons ?? []).filter(
    (s) => s.archived && s.id !== exclude && (s.stats?.matchesPlayed ?? 0) > 0,
  );
  if (played.length === 0) return null;
  return played.reduce((best, s) =>
    (s.stats?.matchesPlayed ?? 0) > (best.stats?.matchesPlayed ?? 0) ? s : best,
  );
}

/**
 * The day the numbers start, as a sentence fragment.
 *
 * A date rather than "soon". "Soon" is what every unfinished thing says, and a
 * player who reads it learns nothing about whether to come back on Monday.
 *
 * Null when the season has no start date recorded — in which case the caller
 * says the same thing without one rather than inventing a day.
 */
export function firstResultsOn(startsOn: string | null | undefined): string | null {
  if (!startsOn) return null;
  const date = new Date(startsOn);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    /*
     * Read as UTC, because the stored value is a DATE and arrives as midnight
     * UTC. Rendered in the viewer's own zone it slips to the previous day for
     * anyone BEHIND Greenwich — 13 September becomes the 12th in New York.
     *
     * Not for anyone ahead of it, which is why pinning this to Johannesburg
     * instead would pass every test written from a South African desk and still
     * be wrong for a player watching from abroad. UTC is right for all of them.
     */
    timeZone: 'UTC',
  });
}
